/**
 * Reading a CSV file into rows of raw cells.
 *
 * RFC 4180 plus the two things real files do that it doesn't mention: a UTF-8 BOM (Excel writes
 * one) and CRLF line endings. Everything else is deliberately literal — a bare quote in the
 * middle of an unquoted cell is a quote character, not the start of a quoted section, because
 * `6" screen` in a tournament name should not swallow the rest of the file.
 *
 * Cells come back exactly as written, whitespace included. Trimming is the reader's business:
 * a column that means something different with a trailing space is a validation question, not
 * a parsing one.
 */
export function parseCsv(text: string): string[][] {
  const source = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;

  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  let i = 0;

  const endCell = () => {
    row.push(cell);
    cell = '';
  };
  const endRow = () => {
    endCell();
    rows.push(row);
    row = [];
  };

  while (i < source.length) {
    const ch = source[i];

    if (quoted) {
      // "" inside a quoted cell is one literal quote; a lone one ends the quoting.
      if (ch === '"') {
        if (source[i + 1] === '"') {
          cell += '"';
          i += 2;
          continue;
        }
        quoted = false;
        i += 1;
        continue;
      }
      cell += ch;
      i += 1;
      continue;
    }

    if (ch === '"' && cell.length === 0) {
      quoted = true;
      i += 1;
      continue;
    }
    if (ch === ',') {
      endCell();
      i += 1;
      continue;
    }
    if (ch === '\r') {
      endRow();
      i += source[i + 1] === '\n' ? 2 : 1;
      continue;
    }
    if (ch === '\n') {
      endRow();
      i += 1;
      continue;
    }

    cell += ch;
    i += 1;
  }

  // Only when the file did not end on a newline — otherwise every well-formed file would
  // finish with a phantom row of one empty cell.
  if (cell.length > 0 || row.length > 0) endRow();

  return rows;
}

/** A row of nothing but empty cells — what a stray newline or a spacer line in a sheet leaves. */
export const isBlankRow = (row: readonly string[]): boolean =>
  row.every((cell) => cell.trim() === '');
